/**
 * The new-experiment flow's state: the connection, what to test (step 2), and
 * the experiment form. Local to the page — it is a draft of one form — and
 * every default is derived on render: the only connection, the one the agent's
 * last experiment used, that experiment's dataset, evaluators and mapping.
 */

import { useQueries } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { evaluatorVersionsPath } from "@/features/evaluators/hooks";
import type { Evaluator } from "@/features/evaluators/types";
import { useState } from "react";
import { useConnectors } from "@/features/connectors/hooks";
import type { Agent } from "@/features/agents/types";
import { useMe } from "@/features/auth/hooks";
import { PRODUCTION, type TargetDraft } from "@/features/connectors/types";
import { useTarget } from "@/features/connectors/use-target";
import { useDatasets } from "@/features/datasets/hooks";
import { useEvaluatorPaths, useEvaluators } from "@/features/evaluators/hooks";
import { useDatasetItems } from "@/features/datasets/hooks";
import { toLocalInput } from "@/features/traces/period";
import { newPeriodChoice, type PeriodChoice } from "@/features/traces/period-picker";
import { useConnections } from "@/features/settings/hooks";
import type { Connection } from "@/features/settings/types";
import { canRun, useEvaluatorFixes } from "../scoring/evaluator-overrides";
import { useExperiments } from "../hooks";
import { useInputMapping } from "./input-mapping-step";
import type { ExperimentSummary } from "../types";
import { costError, defaultDataset, defaultSelection, useExperimentForm } from "./use-experiment-form";
import type { FormDraft, WizardDraft } from "./wizard-draft";
import { useVersionChoice } from "./pulls/hooks";

export interface Prefill {
  /** Start from this agent (its page's "New experiment"). */
  agent?: Agent | undefined;
  /** Edit this saved draft; its target stays, its connection and version may change. */
  draft?: ExperimentSummary | undefined;
  /** Duplicate this experiment: every choice copied, saved as a new one named `copyName`. */
  copy?: { of: ExperimentSummary; name: string } | undefined;
}

function defaultConnection(list: Connection[], agent: Agent | undefined, lastId: string | null | undefined) {
  const reaches = (c: Connection) =>
    !!agent && c.connector_type === agent.connector_type && c.base_url === agent.base_url;
  return (
    list.find((c) => c.id === lastId)?.id ??
    list.find(reaches)?.id ??
    (list.length === 1 ? list[0]!.id : null)
  );
}

export function useNewExperiment({ agent, draft, copy }: Prefill, saved: WizardDraft | null = null) {
  const maxCost = useMe().data?.max_experiment_cost_usd ?? 25;
  // A draft is edited, a copy starts as one: either way its choices fill the flow.
  const base = draft ?? copy?.of;
  const copied: Partial<FormDraft> = copy
    ? { name: copy.name, runner: copy.of.runner === "dataset_output" ? "outputs" : "agent" }
    : {};
  const form = useExperimentForm(base, maxCost, { ...copied, ...saved?.form });
  const connections = useConnections();
  const connectors = useConnectors();
  const recent = useExperiments({ agent_id: agent?.id }, !!agent && !base);
  const last = base ? undefined : recent.data?.[0];

  const [picked, setPicked] = useState<string | null>(
    saved?.connectionId ?? base?.agent?.connection_id ?? null,
  );
  const [targetDraft, setTargetDraft] = useState<TargetDraft>(
    saved?.target ?? { version: base?.agent_version ?? PRODUCTION },
  );
  // Trace sources (Langfuse) cannot run an agent; only agent platforms are offered.
  const list = (connections.data ?? []).filter(
    (c) => connectors.data?.find((k) => k.type === c.connector_type)?.kind !== "traces",
  );
  const connectionId = picked ?? defaultConnection(list, agent, last?.agent?.connection_id);
  const connection = list.find((c) => c.id === connectionId);
  const connector = connectors.data?.find((c) => c.type === connection?.connector_type);
  const target = useTarget(connection, connector, targetDraft, agent);
  const agentId = target.resolved.agentId;

  const datasets = useDatasets();
  const datasetId = form.datasetId ?? defaultDataset(datasets.data ?? [], recent.data ?? []);
  const dataset = datasets.data?.find((d) => d.id === datasetId);
  // A live dataset's items are pulled per run and hold the traces' own outputs.
  const live = dataset?.kind === "live";
  const runner = form.runner ?? (live ? "outputs" : "agent");
  // A copy pulls the period its original pulled, as a custom range.
  const [period, setPeriod] = useState<PeriodChoice>(() => {
    if (saved?.live?.period) return saved.live.period;
    const p = base?.pull?.period;
    if (!p) return newPeriodChoice();
    const local = (iso: string) => toLocalInput(new Date(iso));
    return { key: "custom", custom: { start: local(p.start), end: local(p.end) } };
  });
  // A live dataset runs on fresh traces or a version pulled before; a duplicate starts on its original's.
  const liveVersion = useVersionChoice({
    datasetId,
    live,
    original: base?.dataset?.id === datasetId ? base?.dataset?.version : null,
    // A copy of a "Pull next" run pulls next too (a failed one is retried by duplicating it).
    kept:
      saved?.live ??
      (base?.pull?.continue_from_latest && base.dataset
        ? { datasetId: base.dataset.id, choice: null, next: true }
        : undefined),
  });
  // Fresh traces are pulled at the start: the latest version stands in for the fields they hold.
  const version = (live ? liveVersion.version : undefined) ?? dataset?.latest_version?.version ?? null;
  // A live dataset's version shows the fields its traces hold, for the input mapping.
  const sample = useDatasetItems(datasetId ?? "", datasetId ? version : null, 1).data?.items;
  const hasOutputs = live || !!sample?.some((i) => i.output !== null && i.output !== undefined);
  const scope = { dataset_id: datasetId ?? undefined, agent_id: agentId ?? undefined };
  const catalogue = useEvaluators(scope);
  const held = base?.evaluators ?? [];
  const missing = held.filter(
    (e) => catalogue.isSuccess && !catalogue.data.some((current) => current.id === e.id),
  );
  const historical = useQueries({
    queries: missing.map((e) => ({
      queryKey: [evaluatorVersionsPath(e.id), {}],
      queryFn: () => api.get<Evaluator[]>(evaluatorVersionsPath(e.id)),
    })),
  });
  const pinned = historical.flatMap((query, i) => query.data?.filter((e) => e.id === missing[i]?.id) ?? []);
  const pinnedKeys = new Set(pinned.map((e) => e.key));
  const evaluators = {
    ...catalogue,
    data: [...(catalogue.data ?? []).filter((e) => !pinnedKeys.has(e.key)), ...pinned],
    isPending: catalogue.isPending || historical.some((q) => q.isPending),
    isError: catalogue.isError || historical.some((q) => q.isError),
    error: catalogue.error ?? historical.find((q) => q.isError)?.error ?? null,
  };
  const paths = useEvaluatorPaths(scope, !!datasetId).data ?? [];
  const fixes = useEvaluatorFixes(held);
  const all = evaluators.data ?? [];
  const compatible = new Set(all.filter((e) => canRun(e, fixes.overrides)).map((e) => e.id));
  const fallback = last
    ? new Set(all.filter((e) => last.evaluators.some((previous) => previous.key === e.key)).map((e) => e.id))
    : defaultSelection(all);
  const selected = new Set([...(form.picked ?? fallback)].filter((id) => compatible.has(id)));
  const lastMapping =
    last?.input_mapping && Object.keys(last.input_mapping).length ? last.input_mapping : null;
  const mapping = useInputMapping(
    target.spec.data,
    datasetId,
    version,
    paths,
    form.inputMapping ?? lastMapping,
  );

  return {
    form,
    maxCost,
    limitError: costError(form.costLimit, maxCost),
    connections,
    agentConnections: list,
    connection,
    connector,
    /** The choices to keep in the tab (the step is the page's). */
    kept: {
      connectionId: picked,
      target: targetDraft,
      form: form.snapshot,
      ...(live ? { live: { ...liveVersion.kept, period } } : {}),
    },
    pickConnection: (id: string) => (setPicked(id), setTargetDraft({ version: PRODUCTION })),
    target,
    changeTarget: (patch: Partial<TargetDraft>) => setTargetDraft((t) => ({ ...t, ...patch })),
    datasets,
    datasetId,
    dataset,
    version,
    live,
    hasOutputs,
    /** Who answers each item: the agent, run now, or the dataset's own outputs. */
    runner,
    /** A live dataset's period to pull fresh. */
    period,
    setPeriod,
    /** A live dataset's version to run on: fresh traces (pulled at the start) or an existing one. */
    liveVersion,
    /** The duplicated experiment's name, for "same version as …". */
    copyOf: copy?.of.name,
    evaluators,
    paths,
    fixes,
    selected,
    evaluatorIds: [...selected].sort(),
    mapping,
  };
}

export type NewExperiment = ReturnType<typeof useNewExperiment>;
